import React, { useMemo, useState } from 'react';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import { displayTime } from '../../../utils';
import { BUSINESS_VIEW_NAME, SSD_FEATURE_FLAGS_LIST, ALL_SPEEDS, DEFAULT_SPEED } from '../../../../../utils/constants';
import { chainWalk } from '../../../../../utils/helpers';
import { getBusiness } from '../../../utils';
import { getIdleHC } from '../overview/ShiftHeadcount';
import { isFeatureFlagEnabled } from '../../../utils';
import { getSpeedFromRoles } from '../../../../../utils/roleSpeedUtils';

const findDepartmentHeadcount = (quarterPivot, departmentName, business, plan = null) => {
  return Object.keys(quarterPivot)
    .filter((k) => k.startsWith(departmentName))
    .filter((k) => getBusiness(k, plan) === business)
    .reduce((acc, k) => acc + (quarterPivot[k] || 0), 0);
};

const findHeadcountByBusinessAndSpeed = (quarterPivot, business, speed, roles, plan = null) => {
  return Object.keys(quarterPivot)
    .filter((roleKey) => {
      const belongsToBusiness = getBusiness(roleKey, plan) === business;
      // Extract role name from the role key (ex: outbound__pack__pick2rebinpack_three_hour)
      const roleName = roleKey.includes('__')
        ? roleKey.split('__').pop()
        : roleKey.replace(/^(outbound|sortation|injection)_/, '');
      const roleSpeed = getSpeedFromRoles(roles, roleName);
      return belongsToBusiness && roleSpeed === speed;
    })
    .reduce((acc, k) => acc + (quarterPivot[k] || 0), 0);
};

const ViewHeadcount = ({ plan }) => {
  const isGroceryEnabled = isFeatureFlagEnabled(plan.input.adminSettings, SSD_FEATURE_FLAGS_LIST.groceryEnabled, false);
  const isRushIndirectRulesEnabled = isFeatureFlagEnabled(
    plan.input.adminSettings,
    SSD_FEATURE_FLAGS_LIST.rushIndirectRulesEnabled
  );

  const showEnhancedView = isGroceryEnabled || isRushIndirectRulesEnabled;
  const roles = chainWalk(() => plan.input.ssdShiftPlanInput.roleDetailsList, []);
  const speedsToShow = isRushIndirectRulesEnabled ? ALL_SPEEDS : [DEFAULT_SPEED];

  const [expandedRows, setExpandedRows] = useState([]);
  const updateExpandedRows = (row) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(row)) {
      newExpandedRows.delete(row);
    } else {
      newExpandedRows.add(row);
    }
    setExpandedRows([...newExpandedRows]);
  };

  const headcountAllocationPivot = chainWalk(
    () => JSON.parse(plan.output.serializedOutput).headcountAllocationPivot,
    []
  );

  const headcountAllocation = chainWalk(() => JSON.parse(plan.output.serializedOutput).headcountAllocation, []);

  const data = useMemo(() => {
    if (!headcountAllocationPivot || headcountAllocationPivot.length === 0) {
      return [];
    }

    return headcountAllocationPivot.map((pivot) => {
      const { start_datetime_local, available_headcount, to_break, on_move_or_break, idle_to_process, move_pp_to_pp } =
        pivot;

      const groceryOutbound = findDepartmentHeadcount(pivot, 'outbound', BUSINESS_VIEW_NAME.GROCERY, plan);
      const grocerySortation = findDepartmentHeadcount(pivot, 'sortation', BUSINESS_VIEW_NAME.GROCERY, plan);
      const groceryInjection = findDepartmentHeadcount(pivot, 'injection', BUSINESS_VIEW_NAME.GROCERY, plan);
      const groceryNeeded = groceryOutbound + grocerySortation + groceryInjection;

      const coreOutbound = findDepartmentHeadcount(pivot, 'outbound', BUSINESS_VIEW_NAME.CORE, plan);
      const coreSortation = findDepartmentHeadcount(pivot, 'sortation', BUSINESS_VIEW_NAME.CORE, plan);
      const coreInjection = findDepartmentHeadcount(pivot, 'injection', BUSINESS_VIEW_NAME.CORE, plan);
      const ssdNeeded = coreOutbound + coreSortation + coreInjection;
      const groceryBySpeed = {};
      const ssdBySpeed = {};
      speedsToShow.forEach((speed) => {
        if (isGroceryEnabled) {
          groceryBySpeed[`grocery_${speed}`] = findHeadcountByBusinessAndSpeed(
            pivot,
            BUSINESS_VIEW_NAME.GROCERY,
            speed,
            roles,
            plan
          );
        }
        ssdBySpeed[`ssd_${speed}`] = findHeadcountByBusinessAndSpeed(
          pivot,
          BUSINESS_VIEW_NAME.CORE,
          speed,
          roles,
          plan
        );
      });

      const timeSpecificShift = { start: start_datetime_local, end: start_datetime_local + 1 };
      const timeSpecificIdle = getIdleHC(timeSpecificShift, headcountAllocation);
      const idle = timeSpecificIdle ? timeSpecificIdle.total_hc || 0 : 0;

      const breakMove = (to_break || 0) + (on_move_or_break || 0) + (idle_to_process || 0) + (move_pp_to_pp || 0);

      return {
        start_datetime_local,
        totalAvailable: available_headcount || 0,
        groceryNeeded,
        ssdNeeded,
        ...groceryBySpeed,
        ...ssdBySpeed,
        idle,
        breakMove
      };
    });
  }, [headcountAllocationPivot, headcountAllocation]);

  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  const consolidatedTimeIntervals = [];
  data.forEach((entry, index) => {
    if (
      index === 0 ||
      entry.totalAvailable !== data[index - 1].totalAvailable ||
      entry.groceryNeeded !== data[index - 1].groceryNeeded ||
      entry.ssdNeeded !== data[index - 1].ssdNeeded ||
      speedsToShow.some(
        (speed) =>
          (entry[`grocery_${speed}`] || 0) !== (data[index - 1]?.[`grocery_${speed}`] || 0) ||
          (entry[`ssd_${speed}`] || 0) !== (data[index - 1]?.[`ssd_${speed}`] || 0)
      ) ||
      entry.idle !== data[index - 1].idle ||
      entry.breakMove !== data[index - 1].breakMove
    ) {
      consolidatedTimeIntervals.push(entry);
    }
  });

  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <Table showStripes={true} headerRows={1} stickyHeaderColumn={true} stickyHeaderRow={true} headerColumns={1}>
        <TableRow>
          <TableCell width={120} />
          {consolidatedTimeIntervals.map((entry, index) => {
            const startTime = displayTime(entry.start_datetime_local);
            const endTime =
              index < consolidatedTimeIntervals.length - 1
                ? displayTime(consolidatedTimeIntervals[index + 1].start_datetime_local)
                : displayTime(data[data.length - 1].start_datetime_local);
            const timeString = `${startTime} - ${endTime}`;
            return <TableCell key={index}>{timeString}</TableCell>;
          })}
        </TableRow>
        <TableRow>
          <TableCell>Total Available</TableCell>
          {consolidatedTimeIntervals.map((entry, index) => (
            <TableCell key={index}>{entry.totalAvailable}</TableCell>
          ))}
        </TableRow>
        {showEnhancedView && isGroceryEnabled && (
          <TableRow>
            <TableCell>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>SSD Needed</span>
                <Button type="icon" size="small" onClick={() => updateExpandedRows('ssd')}>
                  {expandedRows.includes('ssd') ? (
                    <Icon tokens={chevronDownSmallTokens}>Collapse rows</Icon>
                  ) : (
                    <Icon tokens={chevronRightSmallTokens}>Expand rows</Icon>
                  )}
                </Button>
              </div>
            </TableCell>
            {consolidatedTimeIntervals.map((entry, index) => (
              <TableCell key={index}>{entry.ssdNeeded}</TableCell>
            ))}
          </TableRow>
        )}

        {showEnhancedView &&
          isGroceryEnabled &&
          expandedRows.includes('ssd') &&
          speedsToShow.map((speed) => (
            <TableRow key={`ssd_${speed}`}>
              <TableCell>({speed})</TableCell>
              {consolidatedTimeIntervals.map((entry, index) => (
                <TableCell key={index}>{entry[`ssd_${speed}`] || 0}</TableCell>
              ))}
            </TableRow>
          ))}

        {showEnhancedView && !isGroceryEnabled && (
          <TableRow>
            <TableCell>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Total Needed</span>
                <Button type="icon" size="small" onClick={() => updateExpandedRows('total')}>
                  {expandedRows.includes('total') ? (
                    <Icon tokens={chevronDownSmallTokens}>Collapse rows</Icon>
                  ) : (
                    <Icon tokens={chevronRightSmallTokens}>Expand rows</Icon>
                  )}
                </Button>
              </div>
            </TableCell>
            {consolidatedTimeIntervals.map((entry, index) => (
              <TableCell key={index}>{entry.groceryNeeded + entry.ssdNeeded}</TableCell>
            ))}
          </TableRow>
        )}

        {showEnhancedView &&
          !isGroceryEnabled &&
          expandedRows.includes('total') &&
          speedsToShow.map((speed) => (
            <TableRow key={`total_${speed}`}>
              <TableCell>({speed})</TableCell>
              {consolidatedTimeIntervals.map((entry, index) => (
                <TableCell key={index}>{(entry[`grocery_${speed}`] || 0) + (entry[`ssd_${speed}`] || 0)}</TableCell>
              ))}
            </TableRow>
          ))}

        {showEnhancedView && isGroceryEnabled && (
          <TableRow>
            <TableCell>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Grocery Needed</span>
                <Button type="icon" size="small" onClick={() => updateExpandedRows('grocery')}>
                  {expandedRows.includes('grocery') ? (
                    <Icon tokens={chevronDownSmallTokens}>Collapse rows</Icon>
                  ) : (
                    <Icon tokens={chevronRightSmallTokens}>Expand rows</Icon>
                  )}
                </Button>
              </div>
            </TableCell>
            {consolidatedTimeIntervals.map((entry, index) => (
              <TableCell key={index}>{entry.groceryNeeded}</TableCell>
            ))}
          </TableRow>
        )}

        {showEnhancedView &&
          isGroceryEnabled &&
          expandedRows.includes('grocery') &&
          speedsToShow.map((speed) => (
            <TableRow key={`grocery_${speed}`}>
              <TableCell>({speed})</TableCell>
              {consolidatedTimeIntervals.map((entry, index) => (
                <TableCell key={index}>{entry[`grocery_${speed}`] || 0}</TableCell>
              ))}
            </TableRow>
          ))}

        {showEnhancedView && (
          <TableRow>
            <TableCell>Idle</TableCell>
            {consolidatedTimeIntervals.map((entry, index) => (
              <TableCell key={index}>{entry.idle}</TableCell>
            ))}
          </TableRow>
        )}
        {showEnhancedView && (
          <TableRow>
            <TableCell>Break/Move</TableCell>
            {consolidatedTimeIntervals.map((entry, index) => (
              <TableCell key={index}>{entry.breakMove}</TableCell>
            ))}
          </TableRow>
        )}
      </Table>
    </div>
  );
};

export default ViewHeadcount;
